import { ChangeDetectionStrategy, Component, effect, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';

export interface SavedFilterFormValue {
  name: string;
  color: string;
  icon: string;
}

/**
 * Diálogo para criar um filtro salvo: nome (texto), cor e ícone.
 * Emite os valores em `save`; a página é quem persiste o filtro atual com esses dados.
 */
@Component({
  selector: 'app-saved-filter-dialog',
  standalone: true,
  imports: [FormsModule, DialogModule, ButtonModule, InputTextModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      header="Salvar filtro"
      [visible]="visible()"
      (visibleChange)="onVisibleChange($event)"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '440px' }"
      appendTo="body">
      <div class="sf-form">
        <label class="sf-label">Nome</label>
        <input
          pInputText
          class="sf-name"
          [(ngModel)]="name"
          maxlength="40"
          placeholder="Ex: Meus urgentes" />

        <label class="sf-label">Cor</label>
        <div class="sf-colors">
          @for (c of colors; track c) {
            <button
              type="button"
              class="sf-color"
              [class.sf-color--active]="color === c"
              [style.background]="c"
              (click)="color = c"
              [attr.aria-label]="c"></button>
          }
        </div>

        <label class="sf-label">Ícone</label>
        <div class="sf-icons">
          @for (ic of icons; track ic) {
            <button
              type="button"
              class="sf-icon"
              [class.sf-icon--active]="icon === ic"
              (click)="icon = ic">
              <i [class]="ic"></i>
            </button>
          }
        </div>

        <div class="sf-preview">
          <span class="sf-preview-label">Prévia:</span>
          <span class="sf-chip" [style.background]="color">
            <i [class]="icon"></i>
            {{ name.trim() || 'Nome do filtro' }}
          </span>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button
          pButton
          type="button"
          label="Cancelar"
          severity="secondary"
          (click)="onVisibleChange(false)"></button>
        <button
          pButton
          type="button"
          label="Salvar"
          [disabled]="!name.trim()"
          (click)="onSave()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      .sf-form {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .sf-label {
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--v-text-muted, #64748b);
        margin-top: 6px;
      }
      .sf-name {
        width: 100%;
      }
      .sf-colors {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .sf-color {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        border: 2px solid transparent;
        cursor: pointer;
        padding: 0;
      }
      .sf-color--active {
        border-color: var(--v-text-primary, #0a2540);
        box-shadow: 0 0 0 2px #fff inset;
      }
      .sf-icons {
        display: grid;
        grid-template-columns: repeat(8, 1fr);
        gap: 6px;
      }
      .sf-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 32px;
        border-radius: 6px;
        border: 1px solid var(--v-border, #e2e8f0);
        background: var(--v-bg-card, #fff);
        cursor: pointer;
        color: var(--v-text-primary, #334155);
      }
      .sf-icon--active {
        border-color: var(--color-primary-blue, #1b75bc);
        background: rgba(27, 117, 188, 0.12);
      }
      .sf-preview {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 10px;
      }
      .sf-preview-label {
        font-size: 0.8rem;
        color: var(--v-text-muted, #64748b);
      }
      .sf-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        border-radius: 999px;
        color: #fff;
        font-size: 0.8rem;
        font-weight: 600;
      }
    `,
  ],
})
export class SavedFilterDialogComponent {
  readonly visible = model(false);
  readonly save = output<SavedFilterFormValue>();

  name = '';
  color = '';
  icon = '';

  constructor() {
    effect(() => {
      if (this.visible()) this.reset();
    });
  }

  readonly colors = [
    '#2563eb',
    '#16a34a',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#ec4899',
    '#0a2540',
    '#7ed321',
    '#64748b',
  ];

  readonly icons = [
    'pi pi-star',
    'pi pi-heart',
    'pi pi-flag',
    'pi pi-bookmark',
    'pi pi-tag',
    'pi pi-bolt',
    'pi pi-clock',
    'pi pi-check-circle',
    'pi pi-times-circle',
    'pi pi-exclamation-triangle',
    'pi pi-eye',
    'pi pi-lock',
    'pi pi-bell',
    'pi pi-filter',
    'pi pi-search',
    'pi pi-inbox',
    'pi pi-send',
    'pi pi-user',
    'pi pi-users',
    'pi pi-calendar',
    'pi pi-comment',
    'pi pi-cog',
    'pi pi-circle',
    'pi pi-th-large',
  ];

  private reset(): void {
    this.name = '';
    this.color = this.colors[0];
    this.icon = this.icons[0];
  }

  onVisibleChange(value: boolean): void {
    this.visible.set(value);
  }

  onSave(): void {
    const name = this.name.trim();
    if (!name) return;
    this.save.emit({ name, color: this.color, icon: this.icon });
    this.onVisibleChange(false);
  }
}
